import { http } from "api/http";

// Filed GST return PDFs (GSTR-1, GSTR-3B) of one GST login (gstr_credentials.id).
// Atom backend checks access and forwards to atom-audit, which builds the PDFs and stores them in S3.

export type PdfReturn = "GSTR1" | "GSTR3B";

export interface ReturnPdfMonth {
  period: string; // MMYYYY
  state: "stored" | "fetched" | "not_filed" | "not_applicable" | "failed"; // not_applicable: 1st/2nd month of a filed quarter (quarterly filer)
  kind: "nil" | "summary" | "filed" | null; // GSTR-1: nil (Filed PDF) | summary; GSTR-3B: filed
  message: string | null;
  fetched_at: string | null;
  arn: string | null; // acknowledgement number of the filed return
  filed_date: string | null; // date of filing, dd/mm/yyyy
  due_date: string | null; // due date of the return, dd/mm/yyyy
  system_generated: SystemGeneratedPdf | null; // GSTR-3B only: the portal's "System generated GSTR-3B"; null = not yet
}

export interface SystemGeneratedPdf {
  state: "stored" | "fetched" | "not_available" | "failed";
  message: string | null;
  fetched_at: string | null;
}

export interface ReturnPdfFyResult {
  fy: string;
  ret: PdfReturn;
  gstin: string | null;
  months: ReturnPdfMonth[];
}

// A whole FY = one GST portal login + up to 12 months (~1 min)
const FY_TIMEOUT_MS = 10 * 60 * 1000;

/** Every completed month of the FY: stored months reused, the rest fetched from the GST portal and stored. */
const fetchFyReturnPdfs = async (
  credentialId: number,
  data: { ret: PdfReturn; fy: string; refresh?: boolean }
): Promise<ReturnPdfFyResult> => {
  const res: any = await http.post(`/gstr-return-reports/${credentialId}/fy`, data, { timeout: FY_TIMEOUT_MS });
  return res.data;
};

/** Months of the FY already stored (no GST portal login) — shown as soon as the dialog opens. */
const getFyReturnStatus = async (credentialId: number, params: { ret: PdfReturn; fy: string }): Promise<ReturnPdfFyResult> => {
  const res: any = await http.get(`/gstr-return-reports/${credentialId}/fy-status`, { params });
  return res.data;
};

/** One month again from the GST portal (e.g. a failed month). */
const fetchMonthReturnPdf = (credentialId: number, data: { ret: PdfReturn; period: string; refresh?: boolean }) =>
  http.post(`/gstr-return-reports/${credentialId}`, data, { responseType: "blob", timeout: FY_TIMEOUT_MS });

/** A stored PDF (no GST portal login); systemGenerated: GSTR-3B's system generated one. */
const getStoredReturnPdf = (credentialId: number, params: { ret: PdfReturn; period: string; systemGenerated?: boolean }) =>
  http.get(`/gstr-return-reports/${credentialId}/stored`, { params, responseType: "blob" });

/** ZIP of the FY's stored PDFs of one return. */
const getFyReturnZip = (credentialId: number, params: { ret: PdfReturn; fy: string }) =>
  http.get(`/gstr-return-reports/${credentialId}/fy-zip`, { params, responseType: "blob" });

// ─── Excel: the GST Download page's background job (atom-audit) for the client's saved GST login ───

export interface GstJobUnit {
  ret: string; // GSTR1 | GSTR1A | GSTR2A | GSTR2B | GSTR3B | ANNUAL (GSTR-9 / 9C)
  period: string;
  label: string;
  state: "pending" | "running" | "done" | "failed" | "skipped";
  summary: string | null;
  error: string | null;
  cached?: boolean; // reused from an earlier download
  next_fy?: boolean; // completed month of the next FY (late amendments / ITC of this FY)
}

export interface GstJob {
  job_id: string;
  fy: string;
  selection: Record<string, string[]>;
  state: "queued" | "running" | "done" | "partial" | "failed" | "cancelled" | "interrupted";
  stage: string;
  gstin: string | null;
  legal_name: string | null;
  units: GstJobUnit[];
  started_at: string;
  finished_at: string | null;
  error: string | null;
}

/** Starts the portal fetch of the selected returns (runs in the background on atom-audit). */
const startExcelJob = async (credentialId: number, data: { fy: string; returns: string[] }): Promise<GstJob> => {
  const res: any = await http.post(`/gstr-return-reports/${credentialId}/excel`, data);
  return res.data;
};

/** Newest download of this client's GST login for the FY that includes the return (null when none). */
const getLatestExcelJob = async (credentialId: number, fy: string, ret: string): Promise<GstJob | null> => {
  const res: any = await http.get(`/gstr-return-reports/${credentialId}/excel/latest`, { params: { fy, ret } });
  return res.data || null;
};

const getExcelJob = async (credentialId: number, jobId: string): Promise<GstJob> => {
  const res: any = await http.get(`/gstr-return-reports/${credentialId}/excel/${jobId}`);
  return res.data;
};

const cancelExcelJob = (credentialId: number, jobId: string) =>
  http.post(`/gstr-return-reports/${credentialId}/excel/${jobId}/cancel`);

/** Excel of one return of a finished download. */
const getExcelFile = (credentialId: number, jobId: string, ret: string) =>
  http.get(`/gstr-return-reports/${credentialId}/excel/${jobId}/file`, { params: { ret }, responseType: "blob", timeout: FY_TIMEOUT_MS });

export {
  fetchFyReturnPdfs,
  getFyReturnStatus,
  fetchMonthReturnPdf,
  getStoredReturnPdf,
  getFyReturnZip,
  startExcelJob,
  getLatestExcelJob,
  getExcelJob,
  cancelExcelJob,
  getExcelFile,
};
